/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useAppRollenContext } from "../../../../Basis/Components/AppRollenContext";
import CheckboxInput from "../../../../Basis/Components/CheckboxInput";
import DialogBase from "../../../../Basis/Components/DialogBase";
import { environment } from "../../../../Basis/environment";
import { XIcon } from "../../../../Basis/FontAwesome";
import MitteilungVersionUtil from "../../Commands/MitteilungVersionUtil";
import SetzeMitteilungenAngezeigt from "../../Commands/SetzeMitteilungenAngezeigt";
import SetzeMitteilungenGelesen from "../../Commands/SetzeMitteilungenGelesen";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nMitteilungen";
import Mitteilung from "../../Queries/Mitteilung";
import PromiseLadeDialogMitteilungen from "../../Queries/PromiseLadeDialogMitteilungen";

export default function DialogMitteilungen() {
    const [setzeAlsGelesen, setSetzeAlsGelesen] = useState(false);
    const [mitteilungIndex, setMitteilungIndex] = useState(0);
    const [mitteilungen, setMitteilungen] = useState<Mitteilung[]>([]);
    const appRollenContext = useAppRollenContext();
    const istNutzend = appRollenContext.istNutzend;
    const promiseLadeDialogMitteilungen = new PromiseLadeDialogMitteilungen();
    const setzeMitteilungenAngezeigt = new SetzeMitteilungenAngezeigt();
    const setzeMitteilungenGelesen = new SetzeMitteilungenGelesen();
    const { t } = useTranslation();

    React.useEffect(() => {
        if (!istNutzend || environment.istBuchungsportal) {
            return;
        }

        promiseLadeDialogMitteilungen.execute().then((mitteilungen) => {
            setMitteilungen(mitteilungen ?? []);
            if (mitteilungen && mitteilungen.length > 0) {
                const mitteilungVersionen = MitteilungVersionUtil.mitteilungenZuMitteilungVersionen(mitteilungen);
                setzeMitteilungenAngezeigt.execute({ mitteilungen: mitteilungVersionen });
            }
        });
    }, [istNutzend]);

    function schliesseDialogMitteilungen() {
        if ((mitteilungen.length > 0) && setzeAlsGelesen) {
            const mitteilungVersionen = MitteilungVersionUtil.mitteilungenZuMitteilungVersionen(mitteilungen);
            setzeMitteilungenGelesen.execute({ mitteilungen: mitteilungVersionen });
        }

        setMitteilungen([]);
    }

    return <>
        {mitteilungen.length > 0 &&
            <DialogBase
                sx={{
                    fontSize: "0.875rem",
                    lineHeight: "1.5rem",
                }}
                maxWidth={600}
                open={(mitteilungen.length > 0)}
            >
                <DialogTitle>
                    {t(translationKeys.dialogMitteilungen.mitteilung)} {mitteilungIndex + 1} {t(translationKeys.dialogMitteilungen.von)} {mitteilungen.length}
                    <IconButton
                        sx={{
                            float: "right",
                            height: "32px",
                            width: "32px",
                            padding: "0",
                        }}
                        onClick={() => schliesseDialogMitteilungen()}
                        size="large"
                    >
                        <XIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: "0 1.5rem",
                        minHeight: "400px",
                        maxHeight: "400px",
                    }}
                >
                    <div dangerouslySetInnerHTML={{ __html: mitteilungen[mitteilungIndex].text, }} ></div>
                </DialogContent>
                <DialogActions sx={{ padding: "0.5rem 1.5rem" }}>
                    <CheckboxInput
                        sx={{
                            marginRight: "auto",
                            "& > .lan-checkboxinput__input > .lan-checkboxinput__label > .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                            }
                        }}
                        value={setzeAlsGelesen}
                        onChange={(value) => setSetzeAlsGelesen(value)}
                        caption={t(translationKeys.dialogMitteilungen.nichtMehrAnzeigen)}
                        feldBezeichnung=""
                        typBezeichnung=""
                    />
                    { (mitteilungIndex !== 0) &&
                        <Button onClick={() => setMitteilungIndex(mitteilungIndex - 1)} color="primary">
                            {t(translationKeys.dialogMitteilungen.vorherigeMitteilung)}
                        </Button>
                    }
                    { (mitteilungIndex < mitteilungen.length - 1) &&
                        <Button onClick={() => setMitteilungIndex(mitteilungIndex + 1)} color="primary">
                            {t(translationKeys.dialogMitteilungen.naechsteMitteilung)}
                        </Button>
                    }
                    <Button onClick={() => schliesseDialogMitteilungen()} color="primary">
                        {t(translationKeys.dialogMitteilungen.schliessen)}
                    </Button>
                </DialogActions>
            </DialogBase>
        }
    </>;
}
